<style scoped>
button.passthrough {
    border: none;
    background: none;
}
.modal-content {
    width: min(1200px, 90vw);
}
</style>
<template>
    <div class="modal-content" ref="container">
        <div class="modal-header">
            <h2 class="modal-title">New Group</h2>
            <button type="button" aria-label="Close" class="close icon icon-close" @click.prevent="close" />
        </div>
        <div class="modal-body" style="max-height: calc(100vh - 175px); overflow-y: auto">
            <div class="form-group">
                <label for="newRoleSubtype" class="control-label">Type</label>
                <select id="newRoleSubtype" v-model="newRoleSubtype" class="form-control">
                    <option :value="null">Select a type...</option>
                    <option value="AccessGov">Local</option>
                    <option v-for="type in groupTypes" :value="type.Id">{{ type.DisplayName }}</option>
                </select>
            </div>
            <template v-if="newRoleSubtype == 'AccessGov'">
                <div class="form-group">
                    <label for="newRoleName">Name</label>
                    <input class="form-control" id="newRoleName" type="text" v-model="newRoleName" />
                </div>
                <div class="form-group">
                    <label for="newRoleDescription">Description</label>
                    <input class="form-control" id="newRoleDescription" type="text" v-model="newRoleDescription" />
                </div>
            </template>
            <template v-else-if="newRoleSubtype != null">
                <div class="form-group">
                    <label for="newRoleSearch">Search</label>
                    <input class="form-control" id="newRoleSearch" type="text" placeholder="Starts with..." v-model="newRoleSearch" />
                </div>
                <div>
                    <div class="list-group ad-search-list-group">
                        <p class="list-group-item text-muted bg-light p-4 text-center" v-if="searchResults == null">
                            <span v-if="adSearchProcessing">Searching...</span>
                            <span v-else>Enter a search of least three characters.</span>
                        </p>
                        <p class="list-group-item" v-else-if="searchResults.length == 0">No results found.</p>
                        <p v-else-if="searchResults.length > 0" class="mb-2">Select a group:</p>
                        <label tabindex="0" v-for="result in searchResults" class="d-block list-group-item list-group-item-selectable" :class="{ disabled: !roleNameAvailable(result.Name), selected: groupSelected(result) }">
                            <span v-show="groupSelected(result) && roleNameAvailable(result.Name)" class="ad-search-result-status selected">Selected</span>
                            <span v-show="!roleNameAvailable(result.Name)" class="ad-search-result-status">Added</span>
                            <input type="radio" tabindex="-1" class="sr-only" name="newRoleSelectedSearchResult" v-model="selectedSearchResult" :value="result" :disabled="!roleNameAvailable(result.Name)" />
                            {{ result.DisplayLabel }} ({{ result.Name }})
                        </label>
                    </div>
                </div>
            </template>
        </div>
        <div class="modal-footer">
            <button type="button" class="btn btn-primary" @click.prevent="save">Add</button>
            <AsyncOperationStatus :promise="createPromise" />
            <button type="button" class="btn btn-bare" @click.prevent="close">Cancel</button>
        </div>
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, computed, SetupContext } from "vue";
import { throwIfNonSuccessStatus, fetchXsrf } from "@/lib/fetchHelpers.3";
import AsyncOperationStatus from "@/lib/AsyncOperationStatus.3.vue";
import { dialogSetup, DialogProps } from "@/lib/dialogMixin.3";
import { useMainStore } from "@/mainStore";
import { asyncComputed } from "@vueuse/core";
import { RoleDto } from "@cs/RoleDto";
import { ContainerDto } from "@cs/ContainerDto";
import { PossibleGroupType } from "@cs/PossibleGroupType";
type Props = {
    groupTypes: PossibleGroupType[];
    containerData: ContainerDto;
    createCallback: (role: RoleDto) => Promise<void>;
} & DialogProps;
export default defineComponent({
    components: {
        AsyncOperationStatus,
    },
    props: ["groupTypes", "containerData", "createCallback", "triggerElement"],
    setup(props: Props, { emit }: SetupContext) {
        const containerRef = ref<HTMLElement>(null!);
        dialogSetup(containerRef, props);
        const mainStore = useMainStore();

        const createPromise = ref<Promise<void>>(null!);
        const newRoleName = ref<string>(null!);
        const newRoleSubtype = ref<string>(null!);
        const newRoleDescription = ref<string>(null!);
        // const newRolePermissions = ref([]);
        // const newRoleSubroles = ref([]);
        const newRoleSearch = ref<string[]>([]);
        const selectedSearchResult = ref<RoleDto>(null!);
        const adSearchProcessing = ref(false);
        // const searchResults = ref<Record<string, string>[]>(null!);

        const newRole = computed((): RoleDto | null => {
            if (newRoleSubtype.value == "AccessGov") {
                if (newRoleName.value != null) {
                    return {
                        Name: newRoleName.value,
                        Subtype: newRoleSubtype.value,
                        Description: newRoleDescription.value,
                    } as unknown as RoleDto;
                } else {
                    return null;
                }
            } else if (newRoleSubtype.value != null) {
                return selectedSearchResult.value;
            } else {
                return null;
            }
        });
        const existingRoleNames = computed(() => {
            return props.containerData.Roles.map(i => i.Name);
        });

        const searchResults = asyncComputed(async () => {
            if (newRoleSubtype.value == "AccessGov" || newRoleSearch.value == null || newRoleSearch.value.length < 3) {
                return null;
            }
            adSearchProcessing.value = true;
            // TODO debounce
            const response = await fetchXsrf(
                mainStore,
                await mainStore.getUrl("adminUserManagementGroupsSearch", {
                    groupType: newRoleSubtype.value,
                    search: newRoleSearch.value as any, // eslint-disable-line @typescript-eslint/no-explicit-any
                }),
            );

            throwIfNonSuccessStatus(response);
            const result = await response.json();
            adSearchProcessing.value = false;
            return result;
        });

        const roleNameAvailable = (roleName: string) => {
            return !existingRoleNames.value.includes(roleName);
        };
        const close = () => {
            emit("close");
        };
        const groupSelected = (group: Record<string, string>) => {
            if (selectedSearchResult.value !== null) {
                return selectedSearchResult.value.Name == group.Name;
            }
            return null;
        };
        const save = async () => {
            if (newRole.value == null) {
                alert("Not valid - select an item");
                return;
            }
            if (!roleNameAvailable(newRole.value.Name)) {
                alert("Not valid - name already used");
                return;
            }
            createPromise.value = props.createCallback(newRole.value);
            await createPromise.value;
            emit("close");
        };

        return {
            // Refs
            container: containerRef,

            // States
            createPromise,
            newRoleName,
            newRoleSubtype,
            newRoleDescription,
            newRoleSearch,
            selectedSearchResult,
            adSearchProcessing,

            // Computed
            searchResults,

            // Methods
            roleNameAvailable,
            close,
            groupSelected,
            save,
        };
    },
});
</script>
